import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { AdminHeader, Modal, ConfirmDelete, EditBtn, AddBtn, Field } from "../../components/admin/AdminUI";

const empty = {
  title: "",
  subtitle: "",
  category: "General",
  description: "",
  backgroundImage: "",
  posterImage: "",
  buttonText: "Learn More",
  buttonUrl: "/",
  order: 0,
  active: true,
};

export default function SliderAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/slider").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: k === "order" ? Number(value) : value });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, posterImage: form.posterImage?.trim() || null };
      if (editing) await api.put(`/admin/slider/${editing}`, payload);
      else await api.post("/admin/slider", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      setForm(empty);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const edit = (slide) => {
    setEditing(slide.id);
    setForm({ ...empty, ...slide, posterImage: slide.posterImage || "" });
    setOpen(true);
  };

  const del = async (id) => {
    await api.delete(`/admin/slider/${id}`);
    load();
    toast.success("Deleted");
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, order: items.length + 1 });
    setOpen(true);
  };

  return (
    <div>
      <AdminHeader title="Slider Management" subtitle="Manage homepage hero slides and optional event posters" actions={<AddBtn onClick={openNew} label="New Slide" />} />
      <div className="card-elev overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg text-brand-muted text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Order</th>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left">Category</th>
              <th className="px-4 py-3 text-left">Poster</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border">
            {items.map((slide) => (
              <tr key={slide.id} className="hover:bg-brand-bg/50">
                <td className="px-4 py-3 font-semibold text-brand-text">{slide.order}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-brand-text">{slide.title}</div>
                  <div className="text-xs text-brand-muted truncate max-w-md">{slide.description}</div>
                </td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary-light text-brand-primary font-semibold">{slide.category}</span></td>
                <td className="px-4 py-3 text-brand-muted">{slide.posterImage ? "Enabled" : "None"}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${slide.active ? "bg-brand-accent/10 text-brand-accent" : "bg-slate-100 text-slate-600"}`}>{slide.active ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-3 text-right"><div className="inline-flex items-center gap-1"><EditBtn onClick={() => edit(slide)} /><ConfirmDelete onConfirm={() => del(slide.id)} /></div></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-brand-muted">No managed slides yet. The homepage will keep using the current default hero slides.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Slide" : "New Slide"} size="lg">
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title *"><input required value={form.title} onChange={upd("title")} className="form-input" /></Field>
          <Field label="Subtitle"><input value={form.subtitle} onChange={upd("subtitle")} className="form-input" /></Field>
          <Field label="Category Badge"><input value={form.category} onChange={upd("category")} className="form-input" /></Field>
          <Field label="Display Order"><input type="number" value={form.order} onChange={upd("order")} className="form-input" /></Field>
          <Field label="Background Image *"><input required value={form.backgroundImage} onChange={upd("backgroundImage")} className="form-input" placeholder="/uploads/images/hero.jpg" /></Field>
          <Field label="Poster Image (optional)"><input value={form.posterImage || ""} onChange={upd("posterImage")} className="form-input" placeholder="/uploads/images/cme-poster.jpg" /></Field>
          <Field label="Button Text"><input value={form.buttonText} onChange={upd("buttonText")} className="form-input" /></Field>
          <Field label="Button URL"><input value={form.buttonUrl} onChange={upd("buttonUrl")} className="form-input" /></Field>
          <div className="md:col-span-2"><Field label="Description"><textarea rows={5} value={form.description} onChange={upd("description")} className="form-input" /></Field></div>
          <label className="md:col-span-2 flex items-center gap-2 text-sm font-medium text-brand-text">
            <input type="checkbox" checked={form.active} onChange={upd("active")} className="h-4 w-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary" />
            Active
          </label>
          <div className="md:col-span-2 flex justify-end gap-2"><button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button><button className="btn-primary">{editing ? "Update" : "Create"}</button></div>
        </form>
      </Modal>
    </div>
  );
}
